import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CheckCircle, Envelope, Globe, MapPin, Phone, SignOut, Storefront, User } from '@phosphor-icons/react';
import { mapAuthError, type MappedApiError } from '@mybusinessmate/api-client';
import {
  businessProfileInputSchema,
  profileSettingsFormSchema,
  profileToSettingsValues,
  splitSettingsValues,
  type BusinessProfileResponse,
  type ProfileSettingsFormValues,
} from '@mybusinessmate/schema';
import { Icon } from '../../../../../components/Icon';
import { SettingsSection } from '../../../../../components/settings/settings-section';
import { LogoutDialog } from '../../../../../components/shell/logout-dialog';
import { Avatar } from '../../../../../components/ui/avatar';
import { Banner } from '../../../../../components/ui/banner';
import { Button } from '../../../../../components/ui/button';
import { Card } from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import { Select } from '../../../../../components/ui/select';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { useToast } from '../../../../../components/ui/toast';
import { authClient } from '../../../../../lib/api';
import { profileQueryOptions, saveProfile } from '../../../../../lib/business';
import { INDUSTRY_LABEL, INDUSTRY_OPTIONS } from '../../../../../lib/industries';
import { initialsOf } from '../../../../../lib/initials';
import { normalizeUrl } from '../../../../../lib/onboarding';
import { sessionQueryOptions, useSession, type Session } from '../../../../../lib/session';

export const Route = createFileRoute('/subdomain/app/_client/settings/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const { data: session } = useSession();
  const { data: profile } = useQuery(profileQueryOptions);
  const [logoutOpen, setLogoutOpen] = useState(false);

  return (
    <>
      {profile && session ? (
        <ProfileForm profile={profile} session={session} />
      ) : (
        <div className="flex flex-col gap-(--mbm-space-4)" aria-busy aria-label="Loading your profile">
          <Skeleton className="h-60 rounded-(--mbm-radius-lg)" />
          <Skeleton className="h-96 rounded-(--mbm-radius-lg)" />
        </div>
      )}

      <button
        type="button"
        onClick={() => setLogoutOpen(true)}
        className="inline-flex items-center gap-(--mbm-space-2) self-start rounded-(--mbm-radius-xs) text-label-large text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <Icon icon={SignOut} size="md" />
        Log out
      </button>
      <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </>
  );
}

function ProfileForm({ profile, session }: { profile: BusinessProfileResponse; session: Session }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [formError, setFormError] = useState<string>();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty, dirtyFields, isSubmitting },
  } = useForm<ProfileSettingsFormValues>({
    resolver: zodResolver(profileSettingsFormSchema),
    mode: 'onBlur',
    defaultValues: profileToSettingsValues(profile, session.user.name),
  });

  const industry = watch('industry');

  const onSubmit = handleSubmit(async (values) => {
    setFormError(undefined);
    const { fullName, business } = splitSettingsValues(values);
    const businessChanged = Object.keys(dirtyFields).some((field) => field !== 'fullName');
    try {
      if (dirtyFields.fullName) {
        const { error } = await authClient.updateUser({ name: fullName });
        const mapped = mapAuthError(error);
        if (mapped) throw mapped;
      }
      // `googlePlaceId` rides along unchanged: the server re-derives the stored address from it on every save.
      if (businessChanged) await saveProfile(businessProfileInputSchema.parse(business));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: profileQueryOptions.queryKey }),
        queryClient.invalidateQueries({ queryKey: sessionQueryOptions.queryKey }),
      ]);
      reset(values);
      toast({ title: 'Profile saved' });
    } catch (e) {
      const mapped = e as MappedApiError;
      const first = mapped.fieldErrors?.[0];
      if (first) setError(first.field as keyof ProfileSettingsFormValues, { message: first.message });
      else setFormError(mapped.message ?? 'We couldn’t save your changes. Please try again.');
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-(--mbm-space-6)">
      {formError ? <Banner tone="error">{formError}</Banner> : null}

      <SettingsSection title="You">
        <Card padding="lg" className="flex flex-col gap-(--mbm-space-5)">
          <div className="flex items-center gap-(--mbm-space-4)">
            <Avatar initials={initialsOf(session.user.name, session.user.email)} gradient size="xl" />
            <div className="flex flex-col">
              <span className="text-heading-h3 text-text-primary">{session.user.name}</span>
              {/* TODO(api): add the plan tag (e.g. "Right Hand Mate") once the plan is available. */}
              <span className="text-body-small text-text-tertiary">Owner</span>
            </div>
          </div>
          <div className="grid gap-(--mbm-space-4) border-t border-border-line pt-(--mbm-space-5) sm:grid-cols-2">
            <Input
              label="Full name"
              leadingIcon={User}
              autoComplete="name"
              error={errors.fullName?.message}
              {...register('fullName')}
            />
            <div className="flex flex-col gap-(--mbm-space-2)">
              <Input label="Email" leadingIcon={Envelope} value={session.user.email} disabled readOnly />
              {session.user.emailVerified ? (
                <p className="flex items-center gap-(--mbm-space-1) text-label-small text-feedback-success-text">
                  <Icon icon={CheckCircle} size="sm" />
                  Email verified
                </p>
              ) : null}
            </div>
          </div>
        </Card>
      </SettingsSection>

      <SettingsSection title="Your business">
        <Card padding="lg" className="flex flex-col gap-(--mbm-space-5)">
          <div className="flex items-center gap-(--mbm-space-4)">
            {/* TODO(api): "Change logo" needs a profile field to store the uploaded logo key; not available yet. */}
            <span className="flex size-(--mbm-size-avatar-xl) shrink-0 items-center justify-center rounded-(--mbm-component-radius-card) bg-surface-tint text-heading-h2 text-text-primary">
              {initialsOf(profile.businessName)}
            </span>
            <div className="flex flex-col">
              <span className="text-heading-h3 text-text-primary">{profile.businessName}</span>
              <span className="text-body-small text-text-tertiary">{INDUSTRY_LABEL[industry]}</span>
            </div>
          </div>

          <div className="grid gap-(--mbm-space-4) border-t border-border-line pt-(--mbm-space-5) sm:grid-cols-2">
            <Input
              label="Business name"
              leadingIcon={Storefront}
              autoComplete="organization"
              error={errors.businessName?.message}
              {...register('businessName')}
            />
            <Select label="Category" error={errors.industry?.message} {...register('industry')}>
              {INDUSTRY_OPTIONS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Select>
            {industry === 'other' ? (
              <Input
                containerClassName="sm:col-span-2"
                label="What does your business do?"
                error={errors.customIndustry?.message}
                {...register('customIndustry')}
              />
            ) : null}
            <Input
              label="Business phone"
              type="tel"
              leadingIcon={Phone}
              autoComplete="tel"
              error={errors.contactPhone?.message}
              {...register('contactPhone')}
            />
            <Input
              label="Website"
              leadingIcon={Globe}
              inputMode="url"
              autoComplete="url"
              placeholder="yourbusiness.com"
              error={errors.websiteUrl?.message}
              {...register('websiteUrl', { setValueAs: (v: string) => normalizeUrl(v) })}
            />
            {/* TODO(api): the address comes from the verified Google listing and cannot be edited by hand yet. */}
            <Input
              containerClassName="sm:col-span-2"
              label="Address"
              leadingIcon={MapPin}
              value={profile.addressLine1 ?? ''}
              placeholder="Not set"
              helperText={profile.googlePlaceId ? 'From your Google Business listing.' : undefined}
              disabled
              readOnly
            />
          </div>

          <div className="flex items-center justify-between gap-(--mbm-space-4) border-t border-border-line pt-(--mbm-space-5)">
            <p className="text-label-medium text-tag-amber-text" aria-live="polite">
              {isDirty ? 'You have unsaved changes' : ''}
            </p>
            <div className="flex items-center gap-(--mbm-space-2)">
              <Button variant="ghost" onClick={() => reset()} disabled={!isDirty || isSubmitting}>
                Discard
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
                Save changes
              </Button>
            </div>
          </div>
        </Card>
      </SettingsSection>
    </form>
  );
}
